import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import createIcon from "@/assets/composer-create.png";

// Führende „Erstellen"-Kachel (öffnet den vereinheitlichten Composer/Editor).
function CreateTile({ onCreate, t, stx }) {
  return (
    <button
      type="button"
      data-testid="story-create-tile"
      onClick={onCreate}
      className="flex flex-col items-center gap-1.5 shrink-0 active:scale-[0.95] transition-transform"
    >
      <div className={`w-16 h-16 rounded-full border-2 border-dashed p-[2px] flex items-center justify-center ${stx ? "border-stx-accent bg-stx-panel" : "border-[#0A0A0A] bg-white"}`}>
        <img src={createIcon} alt="" className={`w-8 h-8 object-contain ${stx ? "invert" : ""}`} />
      </div>
      <span className={`text-[10px] w-14 text-center truncate font-medium ${stx ? "text-stx-text" : "text-[#0A0A0A]"}`}>
        {t("story_strip.create", { defaultValue: "Erstellen" })}
      </span>
    </button>
  );
}

export default function StoryStrip({ storyGroups = [], loading = false, canCreate = false, onCreate, area = "SOCIAL" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stx = area === "STX" || area === "STILLTOUCHEDX";
  const viewerLink = (id) => `/stories/viewer/${id}${stx ? "?area=STX" : ""}`;
  const wrap = stx
    ? "flex gap-4 py-4 px-4 border-b border-stx-border bg-stx-bg overflow-x-auto scrollbar-hide"
    : "flex gap-4 py-4 px-4 border-b border-[#E5E5E5] bg-white overflow-x-auto scrollbar-hide";
  const nameCls = stx ? "text-[10px] text-stx-text w-14 text-center truncate font-medium" : "text-[10px] text-[#0A0A0A] w-14 text-center truncate font-medium";
  const ringCls = stx ? "border-stx-accent" : "border-[#0A0A0A]";

  // iter320 · Stummgeschaltete Autoren & ausgeblendete Stories (lokal) herausfiltern.
  let muted, hiddenStories;
  try { muted = new Set(JSON.parse(localStorage.getItem("muted_authors") || "[]")); } catch { muted = new Set(); }
  try { hiddenStories = new Set(JSON.parse(localStorage.getItem("hidden_stories") || "[]")); } catch { hiddenStories = new Set(); }
  const groups = storyGroups.filter((g) => !muted.has(g.author_id) && !hiddenStories.has(g.story?.story_id));

  if (loading) {
    return (
      <div data-testid="story-strip" className={wrap}>
        {canCreate && <CreateTile onCreate={onCreate} t={t} stx={stx} />}
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex flex-col items-center gap-1.5 shrink-0 animate-pulse">
            <div className="w-16 h-16 rounded-full bg-[#F0F0F0] border-2 border-[#E5E5E5] p-[2px]" />
            <span className="text-[10px] text-[#E5E5E5] w-14 text-center truncate bg-[#F0F0F0] rounded">&nbsp;</span>
          </div>
        ))}
      </div>
    );
  }

  // Empty-state: trotzdem die Erstellen-Kachel zeigen, damit der Einstieg bleibt.
  if (groups.length === 0) {
    if (!canCreate) {
      return (
        <div data-testid="story-strip-empty" className={stx ? "py-3 px-4 border-b border-stx-border bg-stx-bg" : "py-3 px-4 border-b border-[#E5E5E5] bg-white"}>
          <p className={stx ? "text-xs text-stx-text-muted" : "text-xs text-[#999]"}>{t("story_strip.empty")}</p>
        </div>
      );
    }
    return (
      <div data-testid="story-strip" className={wrap}>
        <CreateTile onCreate={onCreate} t={t} stx={stx} />
      </div>
    );
  }

  return (
    <div data-testid="story-strip" className={wrap}>
      {canCreate && <CreateTile onCreate={onCreate} t={t} stx={stx} />}
      {groups.map((group) => {
        const author = group.author || {};
        return (
          <button
            key={group.author_id}
            data-testid={`story-avatar-${group.author_id}`}
            onClick={() => navigate(viewerLink(group.profile_id || group.author_id))}
            className="flex flex-col items-center gap-1.5 shrink-0 active:scale-[0.95] transition-transform"
          >
            <div className={`w-16 h-16 rounded-full border-2 ${ringCls} p-[2px]`}>
              {author.avatar_url ? (
                <img src={author.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <div className="w-full h-full rounded-full bg-[#E5E5E5] flex items-center justify-center text-sm font-bold text-[#999]">
                  {(author.display_name || "?")[0]}
                </div>
              )}
            </div>
            <span className={nameCls}>
              {author.display_name || "Creator"}
            </span>
          </button>
        );
      })}
    </div>
  );
}
